'use client';

import React from 'react';
import type { TargetAndTransition, Transition } from 'motion/react';

import { MOTION_TAGS, type MotionTag } from './tags';
import { useDesignMotion } from './useDesignMotion';

export type RevealEffect = 'rise' | 'blur' | 'clip';

/**
 * Where every entrance starts. Shared by all designs so the server HTML (rendered for the
 * default design) is right for whichever design the visitor sees.
 */
const START: Record<RevealEffect, TargetAndTransition> = {
  rise: { opacity: 0, y: 20 },
  blur: { opacity: 0, y: 10, filter: 'blur(12px)' },
  clip: { opacity: 0, scale: 0.94, filter: 'blur(8px)' },
};

export interface RevealProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  'onAnimationStart' | 'onDrag' | 'onDragStart' | 'onDragEnd'
> {
  as?: MotionTag;
  /** `rise` for blocks, `blur` for big titles, `clip` for hero media */
  effect?: RevealEffect;
  /** Extra delay in seconds */
  delay?: number;
  /** Position in a grid: siblings in a row start one design `stagger` apart */
  index?: number;
  /** Grid columns, so each row restarts the stagger (default 3) */
  columns?: number;
  /** Animate on mount instead of when scrolled into view (above-the-fold content) */
  immediate?: boolean;
}

/**
 * Brings content into view once, the way the active design moves: Storybook pops in with a
 * little tilt and overshoot, Nebula rises and settles calmly.
 */
export function Reveal({
  as = 'div',
  effect = 'rise',
  delay = 0,
  index,
  columns = 3,
  immediate = false,
  children,
  ...rest
}: RevealProps) {
  const motion = useDesignMotion();
  const Component = MOTION_TAGS[as];

  const wait = delay + (index === undefined ? 0 : (index % columns) * motion.stagger);
  const at = (transition: Transition): Transition => ({ ...transition, delay: wait });

  const target: Record<RevealEffect, TargetAndTransition> = {
    rise: {
      opacity: 1,
      y: 0,
      // Always end upright and full size: the first render uses the default design's preset,
      // so a tilt it started must still settle when the visitor's design has none
      rotate: motion.reveal.tilt ? [motion.reveal.tilt, 0] : 0,
      scale: motion.reveal.scale ? [motion.reveal.scale, 1] : 1,
      // fade quickly; let position and tilt take the design's time
      transition: { default: at(motion.reveal.transition), opacity: at({ duration: 0.5 }) },
    },
    blur: { opacity: 1, y: 0, filter: 'blur(0px)', transition: at(motion.heading) },
    clip: { opacity: 1, scale: 1, filter: 'blur(0px)', transition: at(motion.clip) },
  };

  const shown = target[effect];

  return (
    <Component
      initial={START[effect]}
      {...(immediate
        ? { animate: shown }
        : { whileInView: shown, viewport: { once: true, margin: '0px 0px -8% 0px' } })}
      {...rest}
    >
      {children}
    </Component>
  );
}

/** Big titles: sharpen out of a blur. */
export function BlurIn(props: Omit<RevealProps, 'effect'>) {
  return <Reveal as='h1' immediate {...props} effect='blur' />;
}

/** Hero media: scale in from slightly smaller. */
export function ClipUp(props: Omit<RevealProps, 'effect'>) {
  return <Reveal {...props} effect='clip' />;
}
